
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import invoiceService from '../services/invoiceService';
import firmService from '../../admin/services/firmService';
import InvoiceForm from '../components/InvoiceForm';
import '../styles/invoices.css';

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Something went wrong. Please try again.';

const normalizeFirm = (result) => {
  if (!result) return null;

  if (result.firm) {
    return result.firm;
  }

  if (Array.isArray(result.firms)) {
    return (
      result.firms.find((firm) => firm.is_active !== false) ||
      result.firms[0] ||
      null
    );
  }

  if (Array.isArray(result)) {
    return (
      result.find((firm) => firm.is_active !== false) ||
      result[0] ||
      null
    );
  }

  if (Array.isArray(result.data?.firms)) {
    return (
      result.data.firms.find((firm) => firm.is_active !== false) ||
      result.data.firms[0] ||
      null
    );
  }

  return result;
};

const normalizeInvoice = (result) => {
  if (!result) return null;

  return result.invoice || result.data?.invoice || result;
};

/*
 * Map the firm API response to the invoice database field names.
 * Adjust the fallback property names if your firm API uses different
 * names for these fields.
 */
const getInvoiceFirmDetails = (firm) => ({
  firm_name: firm.firm_name ?? firm.name ?? '',
  firm_address:
    firm.firm_address ?? firm.address ?? null,
  firm_gst_number:
    firm.firm_gst_number ?? firm.gst_number ?? null,
  firm_pan_number:
    firm.firm_pan_number ?? firm.pan_number ?? null,
  firm_bank_name:
    firm.firm_bank_name ?? firm.bank_name ?? null,
  firm_account_number:
    firm.firm_account_number ?? firm.account_number ?? null,
  firm_ifsc_code:
    firm.firm_ifsc_code ?? firm.ifsc_code ?? null,
});

export default function CreateInvoicePage() {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [loadingFirm, setLoadingFirm] = useState(true);
  const [firmDetails, setFirmDetails] = useState(null);
  const [error, setError] = useState('');
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadFirm = async () => {
      setLoadingFirm(true);
      setError('');

      try {
        // The backend should return the authenticated distributor's firm.
        const result = await firmService.getFirms({
          page: 1,
          limit: 100,
        });

        const firm = normalizeFirm(result);

        if (!cancelled) {
          setFirmDetails(firm);

          if (!firm) {
            setError(
              'No firm was found for your account. Please contact your administrator.'
            );
          } else {
            const invoiceFirm = getInvoiceFirmDetails(firm);

            if (!invoiceFirm.firm_name.trim()) {
              setError(
                'Your firm name is missing. Please update your firm profile.'
              );
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            `Unable to load firm details: ${getErrorMessage(err)}`
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingFirm(false);
        }
      }
    };

    loadFirm();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (payload) => {
    if (saving) return;

    if (!firmDetails) {
      setError('Your firm details are unavailable.');
      return;
    }

    const sellerDetails = getInvoiceFirmDetails(firmDetails);

    if (!sellerDetails.firm_name.trim()) {
      setError(
        'Firm name is missing. Please update your firm profile before creating an invoice.'
      );
      return;
    }

    setSaving(true);
    setError('');

    try {
      /*
       * Include the seller's firm details fetched from Firm Service.
       * The backend must still determine firm ownership from the
       * authenticated user and validate these values.
       */
      const invoicePayload = {
        ...payload,
        ...sellerDetails,
      };

      const result = await invoiceService.createInvoice(invoicePayload);
      const invoice = normalizeInvoice(result);

      if (!invoice) {
        throw new Error(
          'The server did not return the created invoice.'
        );
      }

      setCreatedInvoice(invoice);
    } catch (err) {
      console.error('Invoice creation failed:', {
        status: err.response?.status,
        response: err.response?.data,
        message: getErrorMessage(err),
      });

      setError(getErrorMessage(err));

      // Preserve the existing InvoiceForm error-handling behavior.
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const invoiceNumber = createdInvoice?.invoice_number;

  const handlePreview = () => {
    if (!invoiceNumber) {
      setError(
        'The backend did not return an invoice number. Check the invoice creation API response.'
      );
      return;
    }

    navigate(
      `/user/invoices/${encodeURIComponent(invoiceNumber)}`,
      {
        state: {
          invoice: createdInvoice,
        },
      }
    );
  };

  const handleDownloadPdf = async () => {
    if (!invoiceNumber) {
      setError('Invoice number is not available for PDF download.');
      return;
    }

    setDownloading(true);
    setError('');

    try {
      await invoiceService.downloadInvoicePdf(invoiceNumber);
    } catch (err) {
      setError(`PDF download failed: ${getErrorMessage(err)}`);
    } finally {
      setDownloading(false);
    }
  };

  if (loadingFirm) {
    return (
      <section className="invoices-page">
        <p>Loading your firm details...</p>
      </section>
    );
  }

  return (
    <section className="invoices-page">
      <header className="invoices-page-header">
        <div>
          <Link
            to="/user/invoices"
            className="invoice-back-link"
          >
            ← Back to invoices
          </Link>

          <h1>
            {createdInvoice ? 'Invoice Created' : 'Create Invoice'}
          </h1>

          <p>
            {createdInvoice
              ? 'Your invoice has been created successfully.'
              : 'Enter customer details, select products, and create your invoice.'}
          </p>
        </div>
      </header>

      {error && (
        <div
          className="invoice-alert invoice-alert-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {createdInvoice ? (
        <section className="invoice-form-card">
          <h2>Invoice #{invoiceNumber || 'Created'}</h2>

          <p>
            Customer:{' '}
            {createdInvoice.dealer_name || 'Customer details saved'}
          </p>

          {createdInvoice.total_amount != null && (
            <p>
              Total:{' '}
              {new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(Number(createdInvoice.total_amount))}
            </p>
          )}

          <div className="invoice-form-actions">
            <button
              type="button"
              className="invoice-primary-button"
              onClick={handlePreview}
              disabled={!invoiceNumber}
            >
              Preview Invoice
            </button>

            <button
              type="button"
              className="invoice-secondary-button"
              onClick={handleDownloadPdf}
              disabled={downloading || !invoiceNumber}
            >
              {downloading ? 'Preparing PDF...' : 'Download PDF'}
            </button>

            <Link
              to="/user/invoices"
              className="invoice-back-link"
            >
              View All Invoices
            </Link>
          </div>
        </section>
      ) : firmDetails && getInvoiceFirmDetails(firmDetails).firm_name.trim() ? (
        <InvoiceForm
          key={firmDetails.firm_id || firmDetails.id || 'firm'}
          firmDetails={firmDetails}
          loading={saving}
          onSubmit={handleSubmit}
        />
      ) : (
        <section className="invoice-form-card">
          <p>
            Your firm details are unavailable or incomplete. Please
            check your firm profile before creating an invoice.
          </p>
        </section>
      )}
    </section>
  );
}
